import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFirebase } from "@/contexts/FirebaseContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Book, Calendar, Car, Clock, User } from "lucide-react";
import { getEnrollmentsByUserId } from "@/utils/firestoreService";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { Progress } from "@/components/ui/progress";

const MyEnrollments = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentUser } = useFirebase();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEnrollments = async () => {
      if (!currentUser?.uid) {
        toast({
          title: "Authentication Error",
          description: "Please login to view your enrollments",
          variant: "destructive",
        });
        return;
      }

      setLoading(true);
      try {
        console.log("Fetching enrollments for user:", currentUser.uid);
        const fetchedEnrollments = await getEnrollmentsByUserId(currentUser.uid);
        console.log("Fetched enrollments:", fetchedEnrollments);
        
        const validEnrollments = fetchedEnrollments.filter(enrollment => {
          // Basic validation to ensure required fields exist
          const isValid = enrollment && 
            enrollment.userId === currentUser.uid &&
            enrollment.courseId &&
            enrollment.courseName;
          
          if (!isValid) {
            console.log("Invalid enrollment:", enrollment);
          }
          
          return isValid;
        });
        
        console.log("Filtered valid enrollments:", validEnrollments);
        setEnrollments(validEnrollments);
      } catch (error) {
        console.error("Error fetching enrollments:", error);
        toast({
          title: "Error",
          description: "Failed to load your enrollments. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchEnrollments();
    }
  }, [currentUser, toast]);

  const getProgressPercentage = (enrollment: any) => {
    if (!enrollment.totalHours) return 0;
    return Math.round((enrollment.hoursTaken || 0) / enrollment.totalHours * 100);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "Recent";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="student">
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-12 w-12 mb-4 rounded-full bg-gray-200"></div>
            <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
            <div className="h-4 w-48 bg-gray-200 rounded"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="student">
      <motion.div 
        className="space-y-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div>
          <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-drivewise-700 to-blue-700">My Courses</h1>
          <p className="text-gray-500 mt-2">All courses you've enrolled in</p>
        </div>

        {enrollments.length === 0 ? (
          <motion.div 
            className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-100"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Book className="h-16 w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium">No enrollments found</h3>
            <p className="text-gray-500 mt-2">
              You haven't enrolled in any courses yet.
            </p>
            <Button 
              className="mt-6 bg-gradient-to-r from-drivewise-600 to-blue-600 hover:from-drivewise-700 hover:to-blue-700"
              onClick={() => navigate("/student-dashboard/courses")}
            >
              Browse Courses
            </Button>
          </motion.div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {enrollments.map((enrollment, index) => (
              <motion.div 
                key={enrollment.id} 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <Card className="h-full flex flex-col border border-gray-200 hover:border-drivewise-200 transition-colors overflow-hidden">
                  <div className={`h-2 w-full ${
                    enrollment.courseType === "theory" ? 
                      "bg-gradient-to-r from-blue-500 to-indigo-500" : 
                      "bg-gradient-to-r from-green-500 to-teal-500"
                  }`}></div>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`p-2 rounded-full mr-3 ${
                          enrollment.courseType === "theory" ? "bg-blue-100" : "bg-green-100"
                        }`}>
                          {enrollment.courseType === "theory" ? (
                            <Book className="h-5 w-5 text-blue-600" /> 
                          ) : (
                            <Car className="h-5 w-5 text-green-600" />
                          )}
                        </div>
                        <CardTitle>{enrollment.courseName}</CardTitle>
                      </div>
                      <Badge className={
                        enrollment.status === "completed" ? "bg-green-500" :
                        enrollment.status === "pending" ? "bg-yellow-500" :
                        "bg-blue-500"
                      }>
                        {enrollment.status?.charAt(0).toUpperCase() + enrollment.status?.slice(1)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-grow">
                    <div className="space-y-3 text-sm text-gray-500">
                      <div className="flex items-start">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400 mt-1" />
                        <span>Enrolled: {enrollment.enrolledAt ? formatDate(enrollment.enrolledAt) : "Recent"}</span>
                      </div>
                      
                      {enrollment.totalHours && (
                        <>
                          <div className="flex items-start">
                            <Clock className="h-4 w-4 mr-2 text-gray-400 mt-1" />
                            <div className="flex-1">
                              <div className="flex justify-between mb-1">
                                <span>Course Progress</span>
                                <span>{getProgressPercentage(enrollment)}%</span>
                              </div>
                              <Progress 
                                value={getProgressPercentage(enrollment)} 
                                className="h-2"
                              />
                              <div className="mt-2 text-sm text-gray-600">
                                {enrollment.hoursTaken || 0} of {enrollment.totalHours} hours completed
                                <br />
                                {enrollment.totalHours - (enrollment.hoursTaken || 0)} hours remaining
                              </div>
                            </div>
                          </div>
                          
                          {enrollment.amount && (
                            <div className="mt-2 p-2 bg-gray-50 rounded-md">
                              <div className="text-sm">
                                <span className="font-medium">Course Fee:</span> £{enrollment.amount}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {enrollment.instructorId && enrollment.instructorName && (
                        <div className="flex items-start">
                          <User className="h-4 w-4 mr-2 text-gray-400 mt-1" />
                          <span>Instructor: {enrollment.instructorName}</span>
                        </div>
                      )}
                      
                      {enrollment.preferredTime && (
                        <div className="flex items-start">
                          <Clock className="h-4 w-4 mr-2 text-gray-400 mt-1" />
                          <span>Preferred Time: {enrollment.preferredTime}</span>
                        </div>
                      )}

                      {enrollment.additionalNotes && (
                        <div className="bg-gray-50 p-3 rounded-md mt-3">
                          <p className="text-sm text-gray-700">{enrollment.additionalNotes}</p>
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-6">
                      <Button 
                        className="w-full bg-gradient-to-r from-drivewise-500 to-blue-500 hover:from-drivewise-600 hover:to-blue-600"
                        onClick={() => navigate(
                          enrollment.courseType === "theory" 
                            ? `/student-dashboard/theory-course/${enrollment.courseId}`
                            : `/student-dashboard/practical-course/${enrollment.courseId}`
                        )}
                      >
                        {enrollment.courseType === "theory" ? "Start Course" : "View Details"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </DashboardLayout>
  );
};

export default MyEnrollments;
